import type { Level } from './levels';

// A log line as the query language sees it: the columns of `logs.lines` and the attributes as an object.
export interface LineFields {
    env: string;
    source: string;
    service: string;
    instance: string;
    commit: string;
    level: Level;
    severity: number;
    module: string;
    logger: string;
    message: string;
    kind: string;
    request_id: string;
    user_id: string;
    error_uuid: string;
    exception: string;
    // 16 hex digits (a UInt64 in ClickHouse).
    fingerprint: string;
    http_method: string;
    http_path: string;
    http_status: number;
    duration_ms: number;
    attributes: Record<string, unknown>;
}

// How a column compares:
// - keyword: stored lowercase (env, service, level, kind), compared exactly;
// - keyword-ci: a name as the code writes it (module, logger, exception, method), compared case-insensitively;
// - id: compared exactly (user, request, exception UUID, path);
// - number: compared as numbers, with `>`, `>=`, `<`, `<=`;
// - level: by name or, with an operator, by severity;
// - text: contains, case-insensitive (the message);
// - fingerprint: 16 hex digits.
export type FieldType = 'keyword' | 'keyword-ci' | 'id' | 'number' | 'level' | 'text' | 'fingerprint';

export interface ColumnField {
    name: string;
    column: Exclude<keyof LineFields, 'attributes'>;
    type: FieldType;
    description: string;
}

export const FIELDS: readonly ColumnField[] = [
    { name: 'env', column: 'env', type: 'keyword', description: 'Environment: prod, preprod, demo, staging, local' },
    { name: 'source', column: 'source', type: 'keyword', description: 'The ingestion source (its token)' },
    { name: 'service', column: 'service', type: 'keyword', description: 'api, cron, ms-media, frontend' },
    { name: 'instance', column: 'instance', type: 'keyword', description: "Render's instance" },
    { name: 'commit', column: 'commit', type: 'keyword', description: 'The commit that ran' },
    { name: 'level', column: 'level', type: 'level', description: 'trace, debug, info, warn, error, fatal; level:>=warn' },
    { name: 'module', column: 'module', type: 'keyword-ci', description: "bustard's module (Cron Job, Auth…)" },
    { name: 'logger', column: 'logger', type: 'keyword-ci', description: "bustard's class (CronAuctionsShiftStatesUseCase…)" },
    { name: 'message', column: 'message', type: 'text', description: 'The message, contains' },
    { name: 'kind', column: 'kind', type: 'keyword', description: 'log, request, exception' },
    { name: 'request', column: 'request_id', type: 'id', description: "Render's request id" },
    { name: 'user', column: 'user_id', type: 'id', description: "The user's id" },
    { name: 'error_uuid', column: 'error_uuid', type: 'id', description: 'The exception UUID bustard returns to clients' },
    { name: 'exception', column: 'exception', type: 'keyword-ci', description: "The exception's name" },
    { name: 'fingerprint', column: 'fingerprint', type: 'fingerprint', description: "An error's group" },
    { name: 'method', column: 'http_method', type: 'keyword-ci', description: 'HTTP method' },
    { name: 'path', column: 'http_path', type: 'id', description: 'HTTP path' },
    { name: 'status', column: 'http_status', type: 'number', description: 'HTTP status' },
    { name: 'duration', column: 'duration_ms', type: 'number', description: 'Duration in ms' },
];

const ALIASES: Readonly<Record<string, string>> = {
    msg: 'message',
    context: 'logger',
    class: 'logger',
    user_id: 'user',
    userid: 'user',
    request_id: 'request',
    requestid: 'request',
    uuid: 'error_uuid',
    exception_uuid: 'error_uuid',
    exceptionuuid: 'error_uuid',
    error: 'exception',
    http_method: 'method',
    http_path: 'path',
    http_status: 'status',
    duration_ms: 'duration',
    severity: 'level',
    svc: 'service',
    environment: 'env',
};

const BY_NAME = new Map(FIELDS.map((field) => [field.name, field]));
const SEGMENT = /^[\w@$-]+$/;

export type ResolvedField = { kind: 'column'; field: ColumnField } | { kind: 'attribute'; path: string[] };

export class QueryError extends Error {}

// A known column (by name or alias, any case), else a path into the attributes: `request.headers.user-agent`.
export function resolveField(name: string): ResolvedField {
    const known = BY_NAME.get(ALIASES[name.toLowerCase()] ?? name.toLowerCase());
    if (known) {
        return { kind: 'column', field: known };
    }
    const path = (name.startsWith('attributes.') ? name.slice('attributes.'.length) : name).split('.');
    if (path.some((segment) => !SEGMENT.test(segment))) {
        throw new QueryError(`"${name}" is not a field: use letters, digits, _ - @ $ and dots between names`);
    }

    return { kind: 'attribute', path };
}
